import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
  Grid, Switch, FormControlLabel
} from '@mui/material';
import { Add, Edit, Delete, Home, Waves, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const empty = { streamCode: '', streamName: '' };

const StreamList = () => {
  const navigate = useNavigate();
  const [streams,    setStreams]    = useState([]);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(empty);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [isActive,   setIsActive]   = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchStreams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/academics/streams`, { headers });
      setStreams(res.data);
    } catch { setError('Failed to load streams'); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchStreams(); }, [fetchStreams]);

  const openCreate = () => {
    setEditing(null); setForm(empty); setIsActive(true); setError(''); setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s); setForm({ streamCode: s.streamCode, streamName: s.streamName });
    setIsActive(s.isActive); setError(''); setDialogOpen(true);
  };
  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.streamCode.trim()) { setError('Code is required'); return; }
    if (!form.streamName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/academics/streams/${editing._id}`, { ...form, isActive }, { headers });
        setSuccess('Stream updated');
      } else {
        await axios.post(`${API}/api/academics/streams`, form, { headers });
        setSuccess('Stream created');
      }
      setDialogOpen(false);
      fetchStreams();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = (s) => setConfirmDlg(s);

  const confirmDelete = async () => {
    const s = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/streams/${s._id}`, { headers });
      setSuccess('Deleted'); fetchStreams();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Waves color="primary" />
              <Typography variant="h5" color="primary">Streams</Typography>
              <Chip label={streams.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Academics">
                <IconButton onClick={() => navigate('/academics')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Stream</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Stream Name</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {streams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>No streams defined yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : streams.map(s => (
                <TableRow key={s._id} hover>
                  <TableCell><strong>{s.streamCode}</strong></TableCell>
                  <TableCell>{s.streamName}</TableCell>
                  <TableCell align="center">
                    <Chip label={s.isActive ? 'Active' : 'Inactive'}
                      color={s.isActive ? 'success' : 'default'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(s)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete stream <strong>{confirmDlg?.streamName}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Edit Stream — ${editing.streamCode}` : 'Add Stream'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" name="streamCode" value={form.streamCode} onChange={handle}
                fullWidth size="small" disabled={!!editing}
                inputProps={{ style: { textTransform: 'uppercase' } }}
                helperText="e.g. EAST" />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Stream Name *" name="streamName" value={form.streamName}
                onChange={handle} fullWidth size="small" helperText="e.g. East Stream" />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
                  label={isActive ? 'Active' : 'Inactive'} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StreamList;
